import {
  Avatar,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  IconButton,
  SimpleGrid,
  Spacer,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { FaTrophy } from "react-icons/fa";
import { useAuth } from "../../../contexts/Auth";
import {
  getCommunityHub,
  joinCommunityBookClub,
} from "../../../services/communityApi";
import { trackEvent } from "../../../shared/analytics";
import Spinner from "../../../shared/components/Spinner";
import useStartReadingOrder from "../../../shared/hooks/useStartReadingOrder";
import ReadingOrderCard from "../../browse/components/ReadingOrderCard";
import { formatBookClubSchedule } from "../bookClubSchedule";

const Leaderboard = ({ title, subtitle, board, scoreLabel, info }) => {
  const rows = board?.top || [];
  return (
    <Box bg="gray.700" borderRadius="lg" p={{ base: "4", md: "5" }}>
      <Flex align="start" justify="space-between">
        <Box>
          <Text fontSize="sm" color="gray.400" letterSpacing="wide">
            {title}
          </Text>
          <Heading size="sm" mt="1">
            {subtitle}
          </Heading>
        </Box>
        <Tooltip
          hasArrow
          label={info}
          placement="top-end"
          bg="gray.900"
          color="white"
          arrowShadowColor="gray.900"
        >
          <IconButton
            aria-label={info}
            icon={
              <Text fontSize="xs" lineHeight="1" fontWeight="bold">
                ?
              </Text>
            }
            size="sm"
            variant="ghost"
            color="gray.300"
            borderWidth="1px"
            borderColor="gray.500"
            borderRadius="full"
            minW="5"
            h="5"
            ml="2"
          />
        </Tooltip>
      </Flex>
      <Box mt="3">
        {rows.length ? (
          rows.map((row) => {
            const trophyColor = {
              1: "yellow.300",
              2: "gray.300",
              3: "orange.400",
            }[row.rank];

            return (
              <Flex
                key={row.rank + "-" + row.user?.id}
                py="2"
                borderBottomWidth="1px"
                borderColor="gray.600"
                align="center"
              >
                <Flex w="10" align="center">
                  <Icon as={FaTrophy} boxSize="3.5" color={trophyColor} />
                  <Text ml="1" fontSize="xs" color="white" fontWeight="bold">
                    {row.rank}
                  </Text>
                </Flex>
                <Avatar
                  size="sm"
                  name={row.user?.userHandle}
                  src={row.user?.profilePicture}
                />
                <Box ml="3" flex="1">
                  {row.user?.userHandle ? (
                    <Link to={`/profile/${row.user.userHandle}`}>
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="white"
                        _hover={{
                          textDecoration: "underline",
                          color: "white",
                        }}
                      >
                        {row.user.userHandle}
                      </Text>
                    </Link>
                  ) : (
                    <Text fontSize="sm" fontWeight="semibold">
                      Unknown reader
                    </Text>
                  )}
                </Box>
                <Box textAlign="right">
                  <Text fontSize="sm" fontWeight="bold" color="white">
                    {row.score} {scoreLabel}
                  </Text>
                </Box>
              </Flex>
            );
          })
        ) : (
          <Text fontSize="sm" color="gray.400">
            No scores yet.
          </Text>
        )}
      </Box>
      {board?.me && (
        <Flex mt="3" p="2" bg="gray.800" borderRadius="md" align="center">
          <Flex w="10" align="center">
            <Icon as={FaTrophy} boxSize="3.5" color="red.300" />
            <Text ml="1" color="white" fontSize="xs" fontWeight="bold">
              {board.me.rank}
            </Text>
          </Flex>
          <Avatar
            size="sm"
            name={board.me.user?.userHandle}
            src={board.me.user?.profilePicture}
          />
          <Text ml="3" flex="1" fontSize="sm">
            Your rank
          </Text>
          <Text fontSize="sm" fontWeight="bold">
            {board.me.score} {scoreLabel}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

const leaderboardInfo = {
  streak:
    "Ranks users by consecutive calendar days with at least one comic marked as read. Today or yesterday can start the current streak. Rankings are recalculated every 24 hours.",
  contributors:
    "Ranks contributors across published community orders: 1 point per submitted correction, 5 per created order, and 1 per reader. Redwing is excluded. Rankings are recalculated every 24 hours.",
};

const BookClubSummary = ({ club, onJoin, isJoining, isAuthenticated }) => {
  const navigate = useNavigate();
  if (!club) {
    return (
      <Box bg="gray.700" borderRadius="lg" p="6">
        <Text color="gray.400">The next Book Club is being prepared.</Text>
      </Box>
    );
  }

  return (
    <Box bg="gray.700" borderRadius="lg" overflow="hidden">
      <Flex direction={{ base: "column", md: "row" }}>
        {club.cover && (
          <Box
            w={{ base: "100%", md: "260px" }}
            minH="180px"
            bgImage={`url(${club.cover})`}
            bgSize="cover"
            bgPosition="center"
          />
        )}
        <Box p={{ base: "5", md: "6" }} flex="1">
          <Flex align="center">
            <Box>
              <Text fontSize="sm" color="gray.400">
                BOOK CLUB · {club.status.toUpperCase()}
              </Text>
              <Heading size="md" mt="1">
                {club.title}
              </Heading>
            </Box>
          </Flex>
          <Text mt="4" color="gray.300">
            {club.description}
          </Text>
          <Text mt="3" fontSize="sm" color="gray.400">
            {formatBookClubSchedule(club.startsAt, club.endsAt)}
          </Text>
          <Flex
            mt="5"
            pt="4"
            align={{ base: "start", sm: "center" }}
            justify="space-between"
            gap="3"
            wrap="wrap"
          >
            <Flex gap="3" wrap="wrap">
              {!club.joined && club.status !== "archived" ? (
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={isAuthenticated ? onJoin : () => navigate("/login")}
                  isLoading={isJoining}
                >
                  Join the book club
                </Button>
              ) : (
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={() => navigate("/community/book-club")}
                >
                  Open Book Club
                </Button>
              )}
            </Flex>
            <Text
              ml={{ base: "0", sm: "auto" }}
              color="gray.400"
              fontSize="sm"
              textAlign="right"
            >
              {club.memberCount} members
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

const Community = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const { startReadingOrder } = useStartReadingOrder();
  const queryClient = useQueryClient();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const query = useQuery(
    ["communityHub", timezone],
    () => getCommunityHub(timezone),
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
  const joinMutation = useMutation(joinCommunityBookClub, {
    onSuccess: () => {
      trackEvent("book_club_joined", { source: "community_hub" });
      queryClient.invalidateQueries("communityHub");
      navigate("/community/book-club");
    },
  });

  if (query.isLoading) return <Spinner />;
  if (query.isError)
    return (
      <Container>
        <Text mt="8">Unable to load The Panel right now.</Text>
      </Container>
    );

  const panel = query.data?.data || {};
  const streak = panel.leaderboards?.readingStreak;
  const contributors = panel.leaderboards?.contributorScore;

  return (
    <Container maxW="container.xl" py={{ base: "6", md: "8" }}>
      <Flex
        align={{ base: "start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        mb="8"
      >
        <Box>
          <Text
            color="red.300"
            fontSize="sm"
            letterSpacing="wide"
            fontWeight="bold"
          >
            COMMUNITY
          </Text>
          <Heading mt="1">The Panel</Heading>
          <Text mt="2" color="gray.400">
            Redwing's community space for shared reading, Book Club discussions,
            and finding your next order.
          </Text>
        </Box>
      </Flex>

      <Text fontSize="sm" color="gray.400" letterSpacing="wide" mb="3">
        LEADERBOARDS
      </Text>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="4">
        <Leaderboard
          title="READING STREAK"
          subtitle="Keep the run going"
          board={streak}
          scoreLabel="days"
          info={leaderboardInfo.streak}
        />
        <Leaderboard
          title="CONTRIBUTOR SCORE"
          subtitle="Build Redwing with us"
          board={contributors}
          scoreLabel="points"
          info={leaderboardInfo.contributors}
        />
      </SimpleGrid>

      <Flex mt="10" mb="3" align="center">
        <Text fontSize="sm" color="gray.400" letterSpacing="wide">
          BOOK CLUB
        </Text>
        <Spacer />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate("/community/book-club")}
        >
          View details
        </Button>
      </Flex>
      <BookClubSummary
        club={panel.bookClub}
        onJoin={() => joinMutation.mutate()}
        isJoining={joinMutation.isLoading}
        isAuthenticated={!!authData}
      />

      <Flex mt="10" mb="3" align="center">
        <Box>
          <Text fontSize="sm" color="gray.400" letterSpacing="wide">
            FEATURED COMMUNITY ORDERS
          </Text>
          <Text fontSize="sm" color="gray.500" mt="1">
            This week’s three picks from the community.
          </Text>
        </Box>
        <Spacer />
        <Button size="sm" variant="ghost" onClick={() => navigate("/browse")}>
          See full library
        </Button>
      </Flex>
      {panel.featuredOrders?.length ? (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4">
          {panel.featuredOrders.map((order) => (
            <ReadingOrderCard
              key={order.id}
              {...order}
              onClickStart={() => {
                if (!authData) {
                  navigate("/login");
                  return;
                }
                startReadingOrder(order.id);
              }}
              isPremium={true}
              premiumOnly={false}
              readingCount={order.readingCount}
            />
          ))}
        </SimpleGrid>
      ) : (
        <Box bg="gray.700" p="6" borderRadius="lg">
          <Text color="gray.400">This week’s picks are on their way.</Text>
        </Box>
      )}
    </Container>
  );
};

export default Community;
